import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CreateTuple } from './create-tuple'
import { UnsupportedNodeError } from './utils'

/**
 * Counts the number of items that would be in the list returned by the given
 * expression
 *
 * @param expression Expression that returns a list
 * @example
 * // Count the number of departments in `myDepartments` for each query unit
 * { '{}': { departmentCount: { '#': { '@s': 'myDepartments' } } } }
 * // ->
 * { departmentCount: 13 },
 * { departmentCount: 0 },
 * { departmentCount: 4 },
 * ...
 */
export interface Count<out T = any> {
  /** {@link Count} */
  readonly [Key.Count]: T extends number ? Expression<readonly any[]> : never
}
export namespace Count {
  export const isCount: TypeChecker<Count> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Count>({
      [Key.Count]: Expression.isExpression as TypeChecker<Count[Key.Count]>,
    }),
  ).usingAlias('Fn.Count')
  export const assertIsCount: TypeAsserter<Count> =
    TypeAsserter.Factory.from(isCount)

  /**
   * @see {@link Node}
   */
  export class Node<T extends number = number> extends fnNode.Node<
    T,
    Key.Count,
    fnNode.Node
  > {
    public readonly type = common.value.types.Intrinsic.Integer

    constructor(arg: Node<T>['arg']) {
      super(Key.Count, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<Count> = {
    createNode: (fn, context) => {
      const arg = expressionNode.from(fn[Key.Count], context)
      if (expressionNode.ForValue.isForValue(arg)) {
        // If we have a value node, the count is the length of the array. If the
        // value is not an array (whether it is nullish or just mistyped), we
        // return 0
        return new expressionNode.ForValue(
          TypeChecker.isReadonlyArray(arg.value) ? arg.value.length : 0,
        )
      } else if (arg instanceof CreateTuple.Node) {
        // Tuples have a fixed length
        return new expressionNode.ForValue(arg.arg.length)
      } else if (arg instanceof fnNode.Node) {
        return new Node(arg satisfies fnNode.Node as fnNode.Node)
      } else {
        throw new UnsupportedNodeError()
      }
    },
    expressionsInArgs: fn => [fn[Key.Count]],
    typeChecker: isCount,
  }
}
